import { useEffect } from 'react';

import {
    useGetCatalogProductsQuery,
    UpdatedProductsSubscription,
    CatalogProductsFilterInput,
    ProductState,
} from 'data/__generated__';
import { UPDATED_PRODUCTS_SUBSCRIPTION } from 'data/subscriptions/product';
import { locationSource, productArrangementSource, productPriceRangeSource } from 'data/entity-sources/sources';

import { DataTable, type ExtraFilter } from 'components/DataTable';

import { productsColumns, type ProductRow } from '../constants/productsColumns';

const defaultFilters: CatalogProductsFilterInput = {
    states: [ProductState.Regular, ProductState.Created],
};

/**
 * Emplacement, gamme et grille ne se lisent pas sur la ligne produit : ce sont des filtres, pas des
 * colonnes. Les afficher demanderait une requête par produit.
 *
 * Constante de module, comme les colonnes : rien ici ne dépend du rendu. Les trois référentiels sont
 * portés par leur source — la page n'écrit plus ni requête, ni `loadOptions`, ni tri de libellés.
 */
const EXTRA_FILTERS: ExtraFilter[] = [
    { key: 'locationIds', title: 'Emplacement entrepôt', type: 'async-multi-select', source: locationSource },
    { key: 'productArrangementIds', title: 'Gamme', type: 'async-multi-select', source: productArrangementSource },
    { key: 'priceRangeIds', title: 'Grille tarifaire', type: 'async-multi-select', source: productPriceRangeSource },
];

export const ProductsList = () => {
    const {
        loading: productsLoading,
        data: productsData,
        error: productsError,
        fetchMore,
        subscribeToMore,
        refetch,
    } = useGetCatalogProductsQuery({
        variables: {
            offset: 0,
            filters: defaultFilters,
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        const unsubscribe = subscribeToMore<UpdatedProductsSubscription>({
            document: UPDATED_PRODUCTS_SUBSCRIPTION,
            updateQuery: (prevData, { subscriptionData }) => {
                if (!subscriptionData || !prevData.catalogProducts) return prevData;
                const updatedProducts = subscriptionData.data.updatedProducts;
                const updatedProductIds = updatedProducts.map((product) => product._id);
                return {
                    catalogProducts: {
                        ...prevData.catalogProducts,
                        products: [
                            ...updatedProducts,
                            ...prevData.catalogProducts.products.filter(
                                (product) => !updatedProductIds.includes(product._id),
                            ),
                        ],
                    },
                };
            },
        });

        return () => unsubscribe();
    }, [subscribeToMore]);

    if (productsError) {
        throw new Error('Une erreur est survenue lors de la récupération des produits');
    }

    const hasMore = productsData?.catalogProducts.hasMore ?? false;
    const products = productsData?.catalogProducts.products ?? [];

    return (
        <div className="flex h-full w-full flex-col overflow-hidden">
            <DataTable<ProductRow, CatalogProductsFilterInput>
                columns={productsColumns}
                data={products}
                filterMode="server"
                getRowId={(row) => row._id}
                getRowLink={(row) => `/product/${row._id}`}
                hasMore={hasMore}
                isLoading={productsLoading}
                refetch={async (vars) => {
                    await refetch({ ...vars });
                }}
                fetchMore={async (opts) => {
                    await fetchMore({ variables: opts.variables });
                }}
                defaultFilters={defaultFilters}
                extraFilters={EXTRA_FILTERS}
                searchPlaceholder="Nom, marque, volume ou id du produit"
                searchFilterKey="searchValue"
            />
        </div>
    );
};
